import React from 'react';
import { Link } from 'react-router-dom';
import { MapPinIcon as MapPinIconSolid } from '@heroicons/react/24/solid';
import { HeartIcon } from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import edit from "../assets/icons/pencil-01.svg";
import share from "../assets/images/share.svg";
import BackButton from "../SharedComponents/BackButton";

interface Restaurant {
  name: string;
  rate?: number;
  category: string;
  reviewCount?: number;
}

interface RestaurantHeaderProps {
  restaurant: Restaurant;
  showBackButton?: boolean;
  onWriteCommentClick?: () => void;
  onReviewsClick?: () => void;
  onFavoriteClick?: () => void;
  isFavorite?: boolean;
  isFavoriteLoading?: boolean;
  cityName?: string;
}

const StarRating = ({ rating }: { rating: number }) => {
  return (
    <div className="flex items-center gap-1">
      <StarIconSolid className="h-4 w-4 sm:h-3 sm:w-3 text-primary-500" />

      <span className="font-myYekanFaNumMedium text-sm sm:text-base">
        {rating}
      </span>
    </div>
  );
};

const RestaurantHeader: React.FC<RestaurantHeaderProps> = ({
  restaurant,
  showBackButton = false,
  onWriteCommentClick,
  onReviewsClick,
  onFavoriteClick,
  isFavorite = false,
  isFavoriteLoading = false,
  cityName,
}) => {
  return (
    <div className="mb-3 font-myYekanFaNumRegular">
      <div className="flex flex-row justify-between sm:items-center gap-1">

        {/* Restaurant name + rating + reviews */}
        <div className="flex flex-row items-center gap-2">
          {showBackButton && <BackButton reverseArrow />}

          <h1 className="desktop:text-3xl tablet:text-2xl text-[18px] text-black font-myYekanFaNumExtraBold">
            {restaurant.name}
          </h1>

          <div className="flex items-center mr-3 text-xs sm:text-lg gap-1">

            {/* Rating */}
            {restaurant.rate !== undefined && (
              <StarRating rating={restaurant.rate} />
            )}

            {/* Reviews */}
            {restaurant.reviewCount !== undefined && (
              <button
                type="button"
                onClick={onReviewsClick}
                className="font-myYekanFaNumRegular text-xs text-gray-600 hover:text-primary-600 transition-colors cursor-pointer"
              >
                ({restaurant.reviewCount} نظر ثبت شده)
              </button>
            )}

            {cityName && (
              <Link
                to={`/places/city/${encodeURIComponent(cityName)}`}
                className="hidden items-center gap-1 text-xs text-gray-500 transition-colors hover:text-[#038545] desktop:mr-3 desktop:inline-flex"
                aria-label={`مشاهده شهر ${cityName}`}
              >
                <MapPinIconSolid className="h-3.5 w-3.5 text-[#038545]" />
                <span>{cityName}</span>
              </Link>
            )}

          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end space-x-1 space-x-reverse text-xs sm:text-sm font-myYekanMedium tablet:hidden">

          {/* Share */}
          <button className="flex items-center text-black px-1 sm:px-2 py-1">
            <img
              src={share}
              alt="اشتراک گذاری"
              className="w-[20px] h-[20px] sm:w-[32px] sm:h-[32px] ml-1"
            />

            <span className="hidden sm:block">
              اشتراک گذاری
            </span>
          </button>

          {/* Write comment */}
          <button
            onClick={onWriteCommentClick}
            className="flex items-center text-black px-1 sm:px-2 py-1"
          >
            <img
              src={edit}
              alt="ثبت نظر"
              className="w-[20px] h-[20px] sm:w-[32px] sm:h-[32px] ml-1"
            />

            <span className="hidden sm:block">
              ثبت نظر
            </span>
          </button>

          {/* Favorite */}
          <button
            onClick={onFavoriteClick}
            disabled={isFavoriteLoading}
            aria-label={
              isFavorite
                ? "حذف از علاقه‌مندی‌ها"
                : "افزودن به علاقه‌مندی‌ها"
            }
            aria-pressed={isFavorite}
            className="flex items-center text-black disabled:opacity-60"
          >
            <HeartIcon
              className={`w-[24px] h-[24px] sm:w-[32px] sm:h-[32px] ml-1 transition-colors ${isFavorite
                  ? "text-red-500 fill-red-500"
                  : "text-black"
                }`}
            />

            <span className="hidden sm:block">
              {isFavorite
                ? "در علاقه‌مندی‌ها"
                : "افزودن به علاقه‌مندی"}
            </span>
          </button>

        </div>
      </div>
    </div>
  );
};

export default RestaurantHeader;
